import { Link, NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils/cn';
import { AuthMenu } from '@/components/auth/AuthMenu';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { useSession } from '@/hooks/useSession';
import {
  IconBookOpen,
  IconBookmark,
  IconGraduationCap,
} from '@/components/home/icons';

// Single source of truth for the top nav.
// "Qari" was intentionally removed from the primary nav — the Hero's
// "Qari Services" CTA (which routes to /find-qari) is the canonical
// entry point now. The /qari route itself is still alive for direct
// links / bookmarks.
const NAV_ITEMS: { to: string; label: string }[] = [
  { to: '/', label: 'Home' },
  { to: '/surah', label: 'Surahs' },
  { to: '/reader', label: 'Reader' },
  { to: '/memorize', label: 'Memorize' },
  { to: '/blog', label: 'Blog' },
  { to: '/kids', label: 'Kids' },
];

const utilityIconCls = ({ isActive }: { isActive: boolean }) =>
  cn(
    'inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
    isActive
      ? 'bg-night-700 text-night-50'
      : 'text-night-200 hover:bg-night-700/60 hover:text-night-50',
  );

export function Header() {
  const session = useSession();

  return (
    <header className="sticky top-0 z-30 border-b border-night-500/20 bg-night-900/80 backdrop-blur-md">
      <div className="container-wide flex h-16 items-center justify-between gap-6">
        {/* Brand */}
        <Link
          to="/"
          aria-label="Alhudah, home"
          className="flex shrink-0 items-center gap-3"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl">
            <img
              src="/favicon.png"
              alt=""
              aria-hidden="true"
              className="h-8 w-8 object-contain"
            />
          </span>
          <img
            src="/logo/alhudah-main-logo.png"
            alt="Alhudah"
            className="h-6 w-auto sm:h-7"
          />
        </Link>

        {/* Primary nav */}
        <nav className="hidden flex-1 items-center justify-center gap-1 sm:flex">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                item.to === '/kids'
                  ? cn(
                      'rounded-full px-4 py-2 text-sm font-semibold transition-all',
                      'bg-beam text-beam-ink shadow-[0_10px_30px_rgba(0,229,255,0.22)]',
                      'hover:brightness-105 hover:shadow-[0_14px_36px_rgba(0,229,255,0.28)]',
                      'motion-safe:animate-cta-shake',
                      isActive && 'ring-2 ring-white/20',
                    )
                  : cn(
                      'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-night-700 text-night-50'
                        : 'text-night-200 hover:bg-night-700/60 hover:text-night-50',
                    )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Utility cluster — Teacher icon removed alongside the Qari
            nav link; Student (graduation cap) stays as the single
            role-area shortcut. */}
        <div className="flex shrink-0 items-center gap-1">
          <NavLink
            to="/surah"
            aria-label="Open the Quran"
            title="Open the Quran"
            className={utilityIconCls}
          >
            <IconBookOpen className="h-5 w-5" />
          </NavLink>
          <NavLink
            to="/student"
            aria-label="Student dashboard"
            title="Student"
            className={utilityIconCls}
          >
            <IconGraduationCap className="h-5 w-5" />
          </NavLink>
          {session.data && (
            <NavLink
              to="/bookmarks"
              aria-label="Bookmarks"
              title="Bookmarks"
              className={utilityIconCls}
            >
              <IconBookmark className="h-5 w-5" />
            </NavLink>
          )}
          <ThemeToggle />
          <AuthMenu />
        </div>
      </div>
    </header>
  );
}
